
interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string,
        type?: string,
        title?: string,
        description?: string,
        url?: string,
        site_name?: string,
        updated_time?: string,
        image?: string,
        image_secure_url?: string,
        image_width?: number,
        image_height?: number,
        image_alt?: string,
        image_type?: string,
        video?: string,
        video_duration?: number,
        video_upload_date?: string,
        video_allow_embed?: boolean,
    };
    twitter?: {
        card?: string,
        title?: string,
        description?: string,
        image?: string,
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

// interface ingredients {
//     heading?: string;
//     subheading?: string;
//     para?: string;
//     items: ListItem[];
// }

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}
interface injectionList {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    // injectionList: injectionList;
    // treatmentAreas1: TreatmentArea[];
    // treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string,
        beforeAfter: string[] | any,
        introductionImage?: string,
    };
    product: {
        id: number,
        title: string,
        price: number,
        description: string,
    };
}

export const diabeticManagement: {
    en: LanguageContent,
    ar: LanguageContent,
    shared: SharedData,
} =
{
    "en": {
        "url": "diabetes-management",
        "meta": {
            "title": "Diabetes Management in Dubai | Elite Body Home",
            "description": "Comprehensive diabetes management program to help control blood sugar levels and prevent complications through personalized care plans.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/diabetes-management/",
            "og": {
                "title": "Diabetes Management in Dubai | Elite Body Home",
                "description": "Personalized diabetes care plans for better blood sugar control and improved quality of life",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-52.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Diabetes Management"],
        "hero": {
            "title": "Diabetes Management in Dubai",
            "subtitle": "Comprehensive Care for Better Health",
            "description": "Maintaining a good level of life and better health with proper management of diabetes. Diabetes is a chronic disease that impacts the way your body converts food into energy, and poor management of it can spiral into serious health complications. Whether you recently received a diabetes diagnosis or have been living with diabetes for years, learning the right steps for diabetes management can make all the difference in your quality of life."
        },
        "howItWorks": {
            "title": "What is Diabetes Management?",
            "description": "Diabetes management is the continuous process of blood glucose level control, of maintaining a good overall state of health and of preventing or minimizing complications of diabetes. Management is based on lifestyle changes, medications to control blood sugar levels, and regular measurement of your blood glucose. It enables individuals with diabetes to live healthier, longer and more fulfilling lives by helping lower risks for heart disease, kidney failure, nerve damage and vision problems."
        },
        "procedureSteps": {
            "heading": "Procedure of Glucose Management",
            "para": "Diagnosis and management of diabetes involve several components depending on the type of diabetes (specifically whether the patient has Type 1, Type 2, or gestational diabetes). It's a multifaceted approach that includes:",
            "steps": [
                {
                    "title": "Initial Assessment",
                    "desc": "Blood tests (such as the A1C test or fasting glucose) to measure blood sugar levels, assessment of patient history and lifestyle factors, and thorough medical examination.",
                    "icon": "fa-solid fa-clipboard-check"
                },
                {
                    "title": "Personalized Care Plan",
                    "desc": "Dietitian will work with you to plan meals and offer nutrition advice. Prescribing medication (insulin injections for Type 1 or oral medication for Type 2).",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "Self-Monitoring",
                    "desc": "Frequent self-testing with glucometer or continuous glucose monitor (CGM). Maintaining a log of daily blood sugars to identify trends.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "Lifestyle Changes",
                    "desc": "Establishing a consistent exercise regime. Eating a balanced diet with whole grains, fruits, vegetables and lean protein. Cutting back on sugar and refined carbohydrates.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "Medication Therapy",
                    "desc": "Type 1 diabetes requires insulin therapy (injections or pump). Type 2 may need oral medications or insulin to control blood sugar levels.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Regular Checkups",
                    "desc": "Regular visits to manage blood sugar, cholesterol and blood pressure. Yearly eye exams, kidney function tests and foot care.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of Diabetes Management",
            "subheading": "Improving health and preventing complications",
            "para": "There are many benefits of effective diabetes management that help improve overall health and prevent long-term complications:",
            "items": [
                {
                    "title": "Better Blood Sugar Control",
                    "desc": "Minimize risk of hyperglycemia and hypoglycemia with stable glucose levels.",
                    "icon": "fa-solid fa-balance-scale"
                },
                {
                    "title": "Reduced Complications",
                    "desc": "Lower risk of heart disease, kidney damage, nerve damage, and vision loss.",
                    "icon": "fa-solid fa-shield-alt"
                },
                {
                    "title": "Improved Energy",
                    "desc": "Experience less fatigue and more consistent energy levels throughout the day.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Better Nutrition",
                    "desc": "Balanced diet and physical fitness for improved quality of life.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "Faster Healing",
                    "desc": "Reduced risk of infections and improved wound healing capabilities.",
                    "icon": "fa-solid fa-band-aid"
                },
                {
                    "title": "Lower Costs",
                    "desc": "Prevent expensive emergency treatments for diabetes complications.",
                    "icon": "fa-solid fa-hand-holding-usd"
                }
            ]
        },
        "eligibility": {
            "heading": "Who Can Benefit?",
            "subheading": "",
            "para": "Everyone with a diagnosis of diabetes can receive a comprehensive management plan. The approach varies by diabetes type:",
            "items": [
                {
                    "title": "Type 1 Diabetics",
                    "desc": "People who need multiple daily insulin injections and frequent blood sugar testing.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Type 2 Diabetics",
                    "desc": "Those who might control their condition with diet, medication or insulin.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Gestational Diabetics",
                    "desc": "Pregnant women needing specialized care for mother and baby health.",
                    "icon": "fa-solid fa-baby"
                },
                {
                    "title": "High Risk Individuals",
                    "desc": "Those with prediabetes or family history can benefit from prevention strategies.",
                    "icon": "fa-solid fa-exclamation-triangle"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation for Diabetes Management",
            "subheading": "",
            "para": "Preparing for management of diabetes involves learning about your condition and making lifestyle changes:",
            "items": [
                {
                    "title": "Understand Your Diagnosis",
                    "desc": "Learn about your diabetes type, symptoms and potential complications.",
                    "icon": "fa-solid fa-book-medical"
                },
                {
                    "title": "Blood Sugar Tracking",
                    "desc": "Begin tracking with either a glucometer or continuous glucose monitor (CGM).",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "Healthcare Team",
                    "desc": "Collaborate with your doctor, dietitian and diabetes educator for a custom plan.",
                    "icon": "fa-solid fa-users"
                },
                {
                    "title": "Organize Supplies",
                    "desc": "Ensure you have enough medications, syringes or insulin pump supplies.",
                    "icon": "fa-solid fa-briefcase-medical"
                },
                {
                    "title": "Mental Preparation",
                    "desc": "Set practical targets and stay focused on necessary changes.",
                    "icon": "fa-solid fa-brain"
                }
            ]
        },
        "results": {
            "heading": "Expected Results",
            "subheading": "",
            "para": "With continuous diabetes management, people can look forward to living healthier, more balanced lives:",
            "items": [
                {
                    "title": "Stable Blood Sugar",
                    "desc": "Maintained levels within target range for better health.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "Reduced Complications",
                    "desc": "Lower risk of kidney damage, heart disease, and neuropathy.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "Improved Fitness",
                    "desc": "Better physical fitness and weight management results.",
                    "icon": "fa-solid fa-running"
                },
                {
                    "title": "Better Quality of Life",
                    "desc": "Reduced diabetes symptoms and improved daily living.",
                    "icon": "fa-solid fa-smile"
                },
                {
                    "title": "Prevent Acute Issues",
                    "desc": "Avoid diabetic ketoacidosis (DKA) or hypoglycemic episodes.",
                    "icon": "fa-solid fa-shield-alt"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Ongoing Care Recommendations",
            "subheading": "",
            "para": "To maintain effective diabetes management:",
            "items": [
                {
                    "title": "Regular Monitoring",
                    "desc": "Continue tracking blood sugar levels and health markers.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "Medication Adherence",
                    "desc": "Take all prescribed medications as directed by your doctor.",
                    "icon": "fa-solid fa-prescription-bottle"
                },
                {
                    "title": "Healthy Lifestyle",
                    "desc": "Maintain balanced diet and regular exercise routine.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "Routine Checkups",
                    "desc": "Keep all scheduled medical appointments and screenings.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How often should I check my blood sugar?",
                    "answer": "Frequency depends on your diabetes type and treatment plan - your doctor will recommend a testing schedule."
                },
                {
                    "question": "Can diabetes be cured?",
                    "answer": "While there's no cure, proper management can control symptoms and prevent complications."
                },
                {
                    "question": "What's the difference between Type 1 and Type 2 diabetes?",
                    "answer": "Type 1 is an autoimmune condition requiring insulin, while Type 2 relates to insulin resistance and may be managed with lifestyle changes and/or medication."
                },
                {
                    "question": "How important is diet in diabetes management?",
                    "answer": "Extremely important - a balanced diet helps maintain stable blood sugar levels and overall health."
                },
                {
                    "question": "What are the warning signs of diabetes complications?",
                    "answer": "Symptoms like increased thirst, frequent urination, vision changes, numbness or slow-healing wounds should prompt immediate medical attention."
                }
            ]
        }
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/dfesfefew-scaled.jpg",
            "beforeAfter": []
        },
        "product": {
            "id": 169,
            "title": "Diabetes Management Program in Dubai",
            "description": "Comprehensive diabetes care including blood sugar monitoring, medication management, and lifestyle counseling.",
            "price": 499
        }
    },
    "ar": {
        "url": "diabetes-management",
        "meta": {
            "title": "إدارة مرض السكري في دبي | ايليت بودي هوم",
            "description": "برنامج متكامل لإدارة مرض السكري يساعد على التحكم في مستويات السكر في الدم والوقاية من المضاعفات من خلال خطط رعاية شخصية.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/diabetes-management",
            "og": {
                "title": "إدارة مرض السكري في دبي | ايليت بودي هوم",
                "description": "خطط رعاية شخصية لتحسين التحكم في سكر الدم وجودة الحياة",
                "image": "https://www.elitebodyhome.com/images/diabetes-management-og.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "إدارة مرض السكري"],
        "hero": {
            "title": "إدارة مرض السكري في دبي",
            "subtitle": "رعاية شاملة لصحة أفضل",
            "description": "نمنحك القوة لتتحكم في مرض السكري، صحتك هي مستقبلك. تبدأ رحلتك للحفاظ على جودة الحياة وصحة أفضل بإدارة فعالة لمرض السكري. السكري هو مرض مزمن يؤثر على طريقة تحويل الطعام إلى طاقة في الجسم، وإذا لم تتم إدارته بالشكل الصحيح، فقد يؤدي إلى مضاعفات صحية خطيرة. سواء كنت قد تم تشخيصك حديثًا بمرض السكري أو تتعامل معه منذ سنوات، فإن تعلم الخطوات الصحيحة لإدارته يمكن أن يُحدث فرقًا كبيرًا في جودة حياتك."
        },
        "howItWorks": {
            "title": "ما معنى إدارة مرض السكري؟",
            "description": "إدارة السكري هي عملية مستمرة تهدف إلى ضبط مستويات السكر في الدم، والحفاظ على الصحة العامة، والوقاية من مضاعفات المرض أو الحد منها. تعتمد إدارة مرض السكري على الأكل الصحي وتغييرات في نمط الحياة، وتناول الأدوية الخاصة بتنظيم مستوى السكر في الدم، بالإضافة إلى القياس المنتظم لمستوى السكر في الدم. تساعد هذه الإدارة الشاملة مرضى السكري على عيش حياة أطول وأكثر صحة، كما تساهم بشكل فعال في تقليل مخاطر الإصابة بأمراض القلب، والفشل الكلوي، وتلف الأعصاب، ومشكلات الرؤية."
        },
        "procedureSteps": {
            "heading": "خطوات إدارة مرض السكري",
            "para": "يتطلب تشخيص وإدارة مرض السكري عدة خطوات تختلف حسب نوع مرض السكري (سواء السكري من النوع الأول أو النوع الثاني أو سكري الحمل):",
            "steps": [
                {
                    "title": "التشخيص والتقييم المبدئي",
                    "desc": "يشمل إجراء تحاليل الدم مثل اختبار HbA1c أو اختبار الجلوكوز الصائم لتحديد مستوى السكر في الدم، ومراجعة التاريخ الطبي للمريض والعوامل المتعلقة بنمط الحياة، بجانب إجراء فحص طبي شامل للكشف عن أي مضاعفات محتملة.",
                    "icon": "fa-solid fa-clipboard-check"
                },
                {
                    "title": "تصميم خطة علاجية",
                    "desc": "تُصمم خطة رعاية فردية تناسب نوع السكري والحالة الصحية لكل مريض، يتعاون خلالها أخصائي التغذية مع المريض لتحديد خطة غذائية مناسبة وتقديم النصائح الغذائية، بجانب وصف العلاج المناسب مثل إبر الإنسولين لمرضى النوع الأول، أو أدوية فموية لمرضى النوع الثاني.",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "متابعة ذاتية دقيقة",
                    "desc": "تعتمد على إجراء قياسات منتظمة لمستوى السكر باستخدام جهاز قياس نسبة السكر أو جهاز مراقبة الجلوكوز المستمر (CGM)، مع الاحتفاظ بسجل يومي للقراءات لتحديد الأنماط وتعديل العلاج عند الحاجة.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "تغييرات إيجابية في نمط الحياة",
                    "desc": "تتضمن الالتزام ببرنامج رياضي منتظم مثل المشي، أو ركوب الدراجة، أو السباحة، واتباع نظام غذائي متوازن يحتوي على الحبوب الكاملة، والفواكه، والخضروات، والبروتينات قليلة الدهون، مع تقليل السكريات والكربوهيدرات المكررة.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "العلاج بالأدوية والإنسولين",
                    "desc": "يستوجب علاج مرضى السكري من النوع الأول بالإنسولين، سواء عبر الإبر أو مضخة الإنسولين، ولكن يمكن استخدام أدوية فموية أو الإنسولين في حالة السكري من النوع الثاني حسب شدة الحالة.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "المتابعة الطبية المنتظمة",
                    "desc": "تتضمن زيارات دورية للطبيب لمتابعة التحكم في سكر الدم ومستوى الكوليسترول، وضغط الدم، بجانب إجراء فحوصات سنوية للعين، ووظائف الكلى، والعناية بالقدمين.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد البرنامج",
            "subheading": "تحسين الصحة والوقاية من المضاعفات",
            "para": "يوفر البرنامج العديد من الفوائد لإدارة السكري بفاعلية، حيث لا يقتصر على تحسين الصحة العامة فحسب، بل يساعد أيضًا على الوقاية من المضاعفات المزمنة طويلة الأمد:",
            "items": [
                {
                    "title": "تحكم أفضل في مستوى السكر",
                    "desc": "الحفاظ على مستويات منتظمة للسكر في الدم يقلل من خطر نوبات ارتفاع السكر أو انخفاضه.",
                    "icon": "fa-solid fa-balance-scale"
                },
                {
                    "title": "تقليل المضاعفات",
                    "desc": "خفض خطر الإصابة بالمضاعفات المزمنة مثل أمراض القلب، وتلف الكلى، واعتلال الأعصاب، وضعف النظر.",
                    "icon": "fa-solid fa-shield-alt"
                },
                {
                    "title": "تحسين مستويات الطاقة",
                    "desc": "زيادة النشاط اليومي وتقليل الشعور بالإرهاق المستمر.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "تعزيز التغذية الصحية",
                    "desc": "نظام غذائي متوازن ونشاط بدني منتظم لتحسين جودة الحياة.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "تسريع التئام الجروح",
                    "desc": "تقليل خطر العدوى وتحسين قدرة الجسم على التعافي.",
                    "icon": "fa-solid fa-band-aid"
                },
                {
                    "title": "توفير التكاليف",
                    "desc": "الوقاية من الحاجة إلى علاجات طارئة مكلفة للمضاعفات.",
                    "icon": "fa-solid fa-hand-holding-usd"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية للبرنامج",
            "subheading": "",
            "para": "يستحق جميع المصابين بمرض السكري الحصول على خطة إدارة شاملة ومتكاملة، مع اختلاف في التفاصيل حسب نوع السكري:",
            "items": [
                {
                    "title": "مرضى السكري من النوع الأول",
                    "desc": "يتطلب جرعات متعددة من الإنسولين يوميًا، بالإضافة إلى مراقبة متكررة لمستويات السكر في الدم على مدار اليوم.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "مرضى السكري من النوع الثاني",
                    "desc": "قد يتمكن البعض من التحكم في المرض من خلال النظام الغذائي، في حين يحتاج آخرون إلى أدوية أو الإنسولين.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "مرض سكري الحمل",
                    "desc": "تحتاج النساء الحوامل إلى رعاية دقيقة تضمن صحة الأم والجنين طوال فترة الحمل.",
                    "icon": "fa-solid fa-baby"
                },
                {
                    "title": "الأشخاص المعرضون للخطر",
                    "desc": "يشمل الأشخاص الذين لديهم تاريخ عائلي مع المرض أو تم تشخيصهم بمقدمات السكري، حيث تُعتبر الاستراتيجيات الوقائية المبكرة ذات فائدة كبيرة.",
                    "icon": "fa-solid fa-exclamation-triangle"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد لإدارة السكري",
            "subheading": "",
            "para": "يتطلب التحضير لإدارة السكري بشكل فعال معرفة عميقة بالحالة الصحية، والحصول على الدعم الطبي المناسب، والقيام بتعديلات دائمة في نمط الحياة:",
            "items": [
                {
                    "title": "فهم التشخيص",
                    "desc": "تعرف على نوع السكري الذي تعاني منه، وما هي الأعراض المرتبطة به، وما المضاعفات المحتملة في حال عدم التحكم به.",
                    "icon": "fa-solid fa-book-medical"
                },
                {
                    "title": "مراقبة مستويات السكر",
                    "desc": "ابدأ باستخدام جهاز قياس السكر المنزلي أو جهاز المراقبة المستمرة للجلوكوز (CGM).",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "التعاون مع الفريق الطبي",
                    "desc": "اعمل جنبًا إلى جنب مع الطبيب، وأخصائي التغذية، واختصاصي التوعية بمرض السكري لوضع خطة علاجية مخصصة تناسب حالتك الصحية.",
                    "icon": "fa-solid fa-users"
                },
                {
                    "title": "تنظيم الأدوية والمستلزمات",
                    "desc": "تأكد من توفر الأدوية اللازمة، والأنسولين، والإبر ومستلزمات مضخة الإنسولين إن وُجدت.",
                    "icon": "fa-solid fa-briefcase-medical"
                },
                {
                    "title": "الاستعداد النفسي",
                    "desc": "حدد أهدافًا واقعية، وخفف من التوتر، وركز على التغييرات الإيجابية التي تحتاج إلى تنفيذها يوميًا.",
                    "icon": "fa-solid fa-brain"
                }
            ]
        },
        "results": {
            "heading": "النتائج المتوقعة",
            "subheading": "",
            "para": "يمكن للأشخاص التمتع بحياة أكثر صحة وتوازنًا على المدى الطويل، من خلال الالتزام بخطة شاملة لإدارة السكري:",
            "items": [
                {
                    "title": "استقرار مستوى السكر",
                    "desc": "الحفاظ على مستويات السكر في الدم مستقرة ضمن المعدلات الصحية.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "تقليل المضاعفات",
                    "desc": "تقليل خطر الإصابة بالمضاعفات المرتبطة بالسكري مثل تلف الكلى، وأمراض القلب، واعتلال الأعصاب.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "تحسين اللياقة البدنية",
                    "desc": "تحسين اللياقة البدنية والسيطرة على الوزن.",
                    "icon": "fa-solid fa-running"
                },
                {
                    "title": "تحسين جودة الحياة",
                    "desc": "تعزيز جودة الحياة والتقليل من الأعراض المزعجة المصاحبة للسكري.",
                    "icon": "fa-solid fa-smile"
                },
                {
                    "title": "الوقاية من المضاعفات الحادة",
                    "desc": "الوقاية من المضاعفات الحادة مثل الحماض الكيتوني السكري (DKA) أو انخفاض السكر المفاجئ.",
                    "icon": "fa-solid fa-shield-alt"
                }
            ]
        },
        "aftercareTips": {
            "heading": "نصائح للرعاية المستمرة",
            "subheading": "",
            "para": "للحفاظ على إدارة فعالة لمرض السكري:",
            "items": [
                {
                    "title": "المراقبة المنتظمة",
                    "desc": "استمر في متابعة مستويات السكر في الدم والمؤشرات الصحية الأخرى.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "الالتزام بالعلاج",
                    "desc": "تناول جميع الأدوية الموصوفة وفقًا لتوجيهات الطبيب.",
                    "icon": "fa-solid fa-prescription-bottle"
                },
                {
                    "title": "نمط حياة صحي",
                    "desc": "حافظ على نظام غذائي متوازن وروتين تمارين رياضية منتظم.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "الفحوصات الدورية",
                    "desc": "احرص على الحضور لجميع المواعيد الطبية والفحوصات المقررة.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "كم مرة يجب أن أقوم بفحص مستوى السكر في الدم؟",
                    "answer": "يختلف التكرار حسب نوع السكري وخطة العلاج - سيوصي طبيبك بجدول زمني مناسب للفحص."
                },
                {
                    "question": "هل يمكن الشفاء من مرض السكري؟",
                    "answer": "لا يوجد علاج نهائي، ولكن الإدارة الصحيحة يمكن أن تتحكم في الأعراض وتمنع المضاعفات."
                },
                {
                    "question": "ما الفرق بين السكري من النوع الأول والنوع الثاني؟",
                    "answer": "النوع الأول هو حالة مناعية تتطلب الأنسولين، بينما النوع الثاني يتعلق بمقاومة الأنسولين وقد يتم التحكم فيه بتغييرات نمط الحياة و/أو الأدوية."
                },
                {
                    "question": "ما أهمية النظام الغذائي في إدارة السكري؟",
                    "answer": "بالغة الأهمية - حيث يساعد النظام الغذائي المتوازن في الحفاظ على مستويات سكر دم مستقرة وصحة عامة أفضل."
                },
                {
                    "question": "ما هي علامات التحذير من مضاعفات السكري؟",
                    "answer": "أعراض مثل العطش الشديد، التبول المتكرر، تغيرات في الرؤية، تنميل أو جروح بطيئة الالتئام تستدعي عناية طبية فورية."
                }
            ]
        }
    }
}